import { Link, useLoaderData } from 'react-router';

import { Badge, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples } from '@/comuns/formato';
import { emRisco } from '@/data/plataformaDaVecchia';
import { dados } from '@/data/porta';
import type { CargaDaPlataforma, ClinicaNaPlataforma, EventoDeAuditoria } from '@/data/tipos';

export async function carregarPlataforma(): Promise<CargaDaPlataforma> {
    return dados().plataforma();
}

/** Quando foi, em palavras curtas. */
export function quandoRelativo(iso?: string): string {
    if (!iso) return 'nunca';
    const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (dias <= 0) return 'hoje';
    if (dias === 1) return 'ontem';
    if (dias < 30) return `há ${dias} dias`;
    const meses = Math.floor(dias / 30);
    return `há ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
}

export const SITUACAO_DA_CLINICA: Record<ClinicaNaPlataforma['situacao'], { rotulo: string; tom: 'success' | 'info' | 'warning' | 'danger' | 'neutral' }> = {
    ativa: { rotulo: 'Ativa', tom: 'success' },
    trial: { rotulo: 'Trial', tom: 'info' },
    inadimplente: { rotulo: 'Inadimplente', tom: 'warning' },
    suspensa: { rotulo: 'Suspensa', tom: 'danger' },
    cancelada: { rotulo: 'Cancelada', tom: 'neutral' },
};

export function Visao() {
    const carga = useLoaderData() as CargaDaPlataforma;
    const risco = carga.clinicas.filter((c) => emRisco(c));

    const colunas: DataTableColumn<ClinicaNaPlataforma>[] = [
        {
            key: 'nome', header: 'Clínica',
            render: (c) => (
                <Link to={`/admin/clinicas/${c.id}`} style={{ color: 'var(--text-primary)', fontWeight: 600, textDecoration: 'none' }}>
                    {c.nome}
                </Link>
            ),
        },
        {
            key: 'situacao', header: 'Situação', width: 140,
            render: (c) => <Badge tone={SITUACAO_DA_CLINICA[c.situacao].tom} dot>{SITUACAO_DA_CLINICA[c.situacao].rotulo}</Badge>,
        },
        { key: 'membrosAtivos', header: 'Pessoas', align: 'right', width: 100 },
        {
            key: 'ultimoAcesso', header: 'Último acesso', align: 'right', width: 150,
            render: (c) => <span style={{ color: c.ultimoAcesso ? 'var(--text-secondary)' : 'var(--danger)' }}>{quandoRelativo(c.ultimoAcesso)}</span>,
        },
    ];

    const colunasDeAuditoria: DataTableColumn<EventoDeAuditoria>[] = [
        { key: 'ocorridoEm', header: 'Quando', width: 150, render: (e) => dataSimples(e.ocorridoEm.slice(0, 10)) },
        { key: 'clinica', header: 'Clínica' },
        { key: 'acao', header: 'Ação' },
        { key: 'autorNome', header: 'Autor', width: 160 },
    ];

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Visão geral"
                description="A base inteira em números. O que cada clínica faz por dentro exige impersonação."
            />

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            {/* A fronteira dita em voz alta: o painel conta, não lê. */}
            <Alert tone="info" dismissible={false} title="O painel vê números, não prontuário">
                A RLS não deixa quem opera a plataforma ler contato, agenda ou ficha de nenhuma clínica. O que
                chega aqui são contagens, e entrar de verdade exige impersonação — com motivo, prazo de 30
                minutos e registro.
            </Alert>

            {risco.length > 0 && (
                <Card title="Clínicas em risco" subtitle="Assinaram e pararam de usar — ou nunca começaram" padding={0}>
                    <DataTable
                        columns={colunas} rows={risco} rowKey="id"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        emptyMessage="Nenhuma clínica em risco."
                    />
                </Card>
            )}

            <Card title="Últimos eventos" subtitle="O log mostra que houve edição, não o que foi editado" padding={0}>
                <DataTable
                    columns={colunasDeAuditoria} rows={carga.auditoria.slice(0, 8)} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum evento registrado."
                />
            </Card>
        </div>
    );
}
